import type {
	DtmOrthoAssignment,
	DtmOrthoFilePair,
	DtmOrthoImportAccepted,
	DtmOrthoImportBody,
	DtmOrthoImportFile,
	DtmOrthoImportInput,
	DtmOrthoImportSelection,
	ImageFileIndex,
	NamedFile,
	StorageNodeRef,
} from "@/components/AddData/dtm-ortho/dtmOrtho.types";
import { importDtmOrtho } from "@/components/AddData/dtm-ortho/dtmOrthoApi";
import { getBaseName, getExtension } from "@/components/AddData/fileName";
import { DEFAULT_SITE_UNITS, type DistanceUnit } from "@/models/siteUnit.types";
import { toApiDistanceUnit } from "@/utils/assetImportDistanceUnit";
import {
	DateFormatDelimiter,
	TimeFormatDelimiter,
	convertToDateString,
	convertToTimeString,
} from "@/utils/dateTimeUtils";
import { getDesign3dBucketInfo } from "@/utils/designApiUtils";
import { uploadLandlogS3 } from "@/utils/fileStorageUtils";
import { toRaw } from "vue";

/** 現場の単位設定を読むために必要な store の形（Vuex の型を持ち込まない） */
export type SiteUnitStore = Readonly<{
	state: Readonly<{
		site?: Readonly<{ units?: Readonly<{ distance?: DistanceUnit }> }>;
	}>;
}>;

/** 取込に使うノード参照の組 */
export interface DtmOrthoStorageNodes {
	readonly terrain: StorageNodeRef;
	readonly image: StorageNodeRef;
}

/**
 * 種別選択ダイアログの確定結果から、origin 付きの割り当てを復元する。
 * ダイアログは props.files[i] そのものを emit する契約なので、imageFile を組の中から
 * 同一性で探して添字を得る。reactive proxy 越しの比較にならないよう両辺を toRaw で剥がす。
 *
 * @throws 組の中に imageFile が見つからないとき（契約上は到達しない）
 */
export const resolveAssignment = (
	pair: DtmOrthoFilePair,
	selection: DtmOrthoImportSelection,
): DtmOrthoAssignment => {
	const imageIndex = findImageIndex(pair.files, selection.assignment.imageFile);
	const terrainIndex: ImageFileIndex = imageIndex === 0 ? 1 : 0;
	if (pair.origin === "local") {
		return {
			origin: "local",
			imageFile: pair.files[imageIndex],
			terrainFile: pair.files[terrainIndex],
		};
	}
	return {
		origin: "storage",
		imageFile: pair.files[imageIndex],
		terrainFile: pair.files[terrainIndex],
	};
};

/** 組の 2 要素のうち imageFile と同一のものの添字（画像の反対側が地形。親 AC-6 / AC-7） */
const findImageIndex = (
	files: readonly [NamedFile, NamedFile],
	imageFile: NamedFile,
): ImageFileIndex => {
	const target = toRaw(imageFile);
	if (toRaw(files[0]) === target) return 0;
	if (toRaw(files[1]) === target) return 1;
	throw new Error("The assigned image file is not part of the tiff pair");
};

/**
 * 種別選択ダイアログに渡す取込単位の初期値（親 AC-47。3 経路で同じ規則。GI-5）。
 * 現場の単位設定は取得済なら 3 値へ正規化済なので、未取得のときだけメートルに倒す。
 */
export const resolveInitialDistanceUnit = (
	store: SiteUnitStore,
): DistanceUnit =>
	store.state.site?.units?.distance ?? DEFAULT_SITE_UNITS.distance;

/** アップロード名に使う拡張子（取れない場合は tiff ペア経路の既定） */
const uploadExtensionOf = (fileName: string): string =>
	getExtension(fileName) ?? "tiff";

/** 既存の点群経路と同じ日時形式（date: YYYY-MM-DD / time: HH:mm:ss。ゼロ埋め、ローカル時刻） */
const nowDateTime = (): { date: string; time: string } => {
	const now = new Date();
	return {
		date: convertToDateString(now, DateFormatDelimiter.HYPHEN),
		time: convertToTimeString(now, TimeFormatDelimiter.COLON),
	};
};

/** 端末上のファイルを Landlog にアップロードし、server に渡すノード参照にする */
const uploadToStorage = async (
	siteId: string,
	file: File,
	type: "pointcloud" | "drawing2dImage",
): Promise<StorageNodeRef> => {
	const bucketInfo = await getDesign3dBucketInfo(siteId, type);
	const bucketId = bucketInfo.data.bucketId;
	const parentNodeId = bucketInfo.data.parentNodeId;
	const uploaded = await uploadLandlogS3(
		bucketId,
		parentNodeId,
		`${getBaseName(file.name)}_${Date.now()}.${uploadExtensionOf(file.name)}`,
		file,
	);
	return {
		bucketId,
		nodeId: uploaded.data.node.id,
		name: file.name,
	};
};

/**
 * 割り当てをサーバに渡すノード参照の組にする。PUT の要否はここだけで分岐する（GI-11 / GI-24）。
 * - local: Landlog へ地形 -> 画像の順に逐次アップロードする。地形が失敗したら画像は送らない
 * - storage: 受け取ったノード参照をそのまま返す。原本は元の場所に残す（GI-35）
 */
export const resolveStorageNodes = async (
	siteId: string,
	assignment: DtmOrthoAssignment,
): Promise<DtmOrthoStorageNodes> => {
	if (assignment.origin === "storage") {
		return { terrain: assignment.terrainFile, image: assignment.imageFile };
	}
	const terrain = await uploadToStorage(
		siteId,
		assignment.terrainFile,
		"pointcloud",
	);
	const image = await uploadToStorage(
		siteId,
		assignment.imageFile,
		"drawing2dImage",
	);
	return { terrain, image };
};

/** Node reference -> one file of the v2 import body */
const toDtmOrthoImportFile = (node: StorageNodeRef): DtmOrthoImportFile => ({
	name: node.name,
	source: {
		type: "ebPlatformNode",
		bucketId: node.bucketId,
		nodeId: node.nodeId,
	},
});

/**
 * Builds the v2 import body from the confirmed input and the resolved node references.
 * The import unit is the user's choice only, converted to the API spelling (C-16).
 */
export const toDtmOrthoImportBody = (
	input: DtmOrthoImportInput,
	nodes: DtmOrthoStorageNodes,
	dateTime: Readonly<{ date: string; time: string }>,
): DtmOrthoImportBody => ({
	terrain: toDtmOrthoImportFile(nodes.terrain),
	image: toDtmOrthoImportFile(nodes.image),
	date: dateTime.date,
	time: dateTime.time,
	distanceUnit: toApiDistanceUnit(input.distanceUnit),
});

/**
 * Starts one DTM / ortho import (network steps shared by the 3 paths).
 * Resolves the nodes (PUT for local files only) and calls the v2 import once. Rows are created
 * only on acceptance, so a failure leaves nothing to clean up (VM-21).
 * Notification / loading / list refresh are left to the caller (GI-13).
 *
 * @param siteId site UUID
 * @param input role assignment (with origin) and import unit
 * @returns ids of the two rows created by the import
 */
export const importDtmOrthoPair = async (
	siteId: string,
	input: DtmOrthoImportInput,
): Promise<DtmOrthoImportAccepted> => {
	const dateTime = nowDateTime();
	const nodes = await resolveStorageNodes(siteId, input.assignment);
	return importDtmOrtho(siteId, toDtmOrthoImportBody(input, nodes, dateTime));
};
